import { CheckCircle2, ChevronDown, Eye, EyeOff, LoaderCircle } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { checkCameraConnection } from '../../api/devices';
import type { Device } from '../../types';
import { ConnectionTest, PasswordField, type ConnectionState } from './FormControls';
import type { DeviceFormProps } from './deviceTypes';

interface CameraFormState {
  name: string;
  ipAddress: string;
  username: string;
  password: string;
  port: string;
  mainStreamUrl: string;
  subStreamUrl: string;
  channelNumber: string;
  dvrId: string;
  onvifProfile: string;
  supportsPtz: boolean;
}

const initialState: CameraFormState = {
  name: '',
  ipAddress: '',
  username: '',
  password: '',
  port: '554',
  mainStreamUrl: '',
  subStreamUrl: '',
  channelNumber: '1',
  dvrId: '',
  onvifProfile: '',
  supportsPtz: false,
};

const rtspAddress = /^rtsp:\/\/.+/i;

export function CameraForm({ recorders, onSubmit, onCancel }: DeviceFormProps & { recorders?: Device[] }) {
  const [form, setForm] = useState<CameraFormState>(initialState);
  const [showStreams, setShowStreams] = useState(false);
  const [error, setError] = useState('');
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [connectionMessage, setConnectionMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const update = <Key extends keyof CameraFormState>(key: Key, value: CameraFormState[Key]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setConnectionState('idle');
    setConnectionMessage('');
    setError('');
  };

  const generatedStreamUrl = () => {
    const username = encodeURIComponent(form.username.trim());
    const password = encodeURIComponent(form.password);
    const rawAddress = form.ipAddress.trim();
    const address = rawAddress.includes(':') && !rawAddress.startsWith('[') ? `[${rawAddress}]` : rawAddress;
    return `rtsp://${username}:${password}@${address}:${form.port || '554'}/`;
  };

  const validateConnectionFields = (forSubmission = false) => {
    const hasExplicitStream = Boolean(form.mainStreamUrl.trim() || form.subStreamUrl.trim());
    if (!form.ipAddress.trim() || (!hasExplicitStream && (!form.username.trim() || !form.password))) {
      setError('Укажите IP-адрес, логин и пароль либо готовый RTSP-адрес.');
      setConnectionState('error');
      return false;
    }
    if (forSubmission && (!form.name.trim() || !form.channelNumber)) {
      setError('Укажите название камеры и номер канала.');
      setConnectionState('error');
      return false;
    }
    const explicitStreams = [form.mainStreamUrl.trim(), form.subStreamUrl.trim()].filter(Boolean);
    if (explicitStreams.some((stream) => !rtspAddress.test(stream))) {
      setError('RTSP-адрес должен начинаться с rtsp://');
      setConnectionState('error');
      return false;
    }
    return true;
  };

  const handleConnectionCheck = async () => {
    setError('');
    if (!validateConnectionFields()) return;
    setConnectionState('checking');
    setConnectionMessage('');
    try {
      const result = await checkCameraConnection({
        ipAddress: form.ipAddress.trim(),
        port: Number(form.port || 554),
        streamUrl: form.mainStreamUrl.trim() || form.subStreamUrl.trim() || generatedStreamUrl(),
      });
      setConnectionState(result.reachable ? 'success' : 'error');
      setConnectionMessage(
        result.reachable
          ? `RTSP ответил за ${Math.max(1, Math.round(result.durationMs))} мс.`
          : result.message || 'Камера не ответила. Проверьте адрес, порт и RTSP-путь.',
      );
    } catch (checkError) {
      setConnectionState('error');
      setConnectionMessage(checkError instanceof Error ? checkError.message : 'Не удалось выполнить проверку.');
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (!validateConnectionFields(true)) return;
    const mainStreamUrl = form.mainStreamUrl.trim() || generatedStreamUrl();
    const subStreamUrl = form.subStreamUrl.trim();

    setIsSubmitting(true);
    try {
      await onSubmit({
        deviceType: 'Camera',
        name: form.name.trim(),
        ipAddress: form.ipAddress.trim(),
        port: Number(form.port),
        mainStreamUrl: mainStreamUrl || undefined,
        subStreamUrl: subStreamUrl || undefined,
        channelNumber: form.channelNumber ? Number(form.channelNumber) : undefined,
        dvrId: form.dvrId || undefined,
        onvifProfile: form.onvifProfile.trim() || undefined,
        supportsPtz: form.supportsPtz,
      });
      onCancel();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Не удалось добавить камеру.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="camera-form" onSubmit={handleSubmit}>
      <div className="camera-form__scroller">
        <div className="camera-form__primary">
          <label className="field-group">
            <span>Название камеры</span>
            <input required type="text" maxLength={200} value={form.name} onChange={(event) => update('name', event.target.value)} placeholder="Например, Главный вход" />
          </label>
          <label className="field-group">
            <span>IP-адрес</span>
            <input required type="text" inputMode="url" value={form.ipAddress} onChange={(event) => update('ipAddress', event.target.value)} placeholder="192.168.1.20" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
          </label>
          <label className="field-group">
            <span>Логин</span>
            <input type="text" value={form.username} onChange={(event) => update('username', event.target.value)} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder="admin" />
          </label>
          <PasswordField label="Пароль" value={form.password} onChange={(value) => update('password', value)} />
          <label className="field-group">
            <span>Канал</span>
            <input required type="number" min="1" inputMode="numeric" value={form.channelNumber} onChange={(event) => update('channelNumber', event.target.value)} />
          </label>
        </div>

        <button className="button button--secondary camera-form__check" type="button" onClick={() => void handleConnectionCheck()} disabled={connectionState === 'checking'}>
          {connectionState === 'checking' ? <LoaderCircle className="spin" size={18} /> : <CheckCircle2 size={18} />}
          {connectionState === 'checking' ? 'Проверяем…' : 'Проверить подключение'}
        </button>

        <ConnectionTest
          state={connectionState}
          successTitle="Камера доступна"
          successMessage={connectionMessage}
          errorTitle="Не удалось подключиться"
          errorMessage={connectionMessage}
        />

        <details className="camera-form__advanced">
          <summary><span>Расширенные настройки</span><ChevronDown size={18} aria-hidden="true" /></summary>
          <div className="camera-form__advanced-body">
            <div className="camera-form__grid">
              <label className="field-group">
                <span>RTSP-порт</span>
                <input required type="number" min="1" max="65535" inputMode="numeric" value={form.port} onChange={(event) => update('port', event.target.value)} />
              </label>
              <label className="field-group">
                <span>Видеорегистратор</span>
                <select value={form.dvrId} onChange={(event) => update('dvrId', event.target.value)}>
                  <option value="">Не назначен</option>
                  {recorders.map((recorder) => <option key={recorder.id} value={recorder.id}>{recorder.name} · {recorder.ip}</option>)}
                </select>
              </label>
            </div>
            <label className="field-group field-group--secret">
              <span>Основной RTSP-поток <small>если путь отличается от стандартного</small></span>
              <input aria-label="Основной RTSP-поток" type={showStreams ? 'text' : 'password'} inputMode="url" maxLength={500} autoComplete="off" autoCapitalize="none" spellCheck={false} value={form.mainStreamUrl} onChange={(event) => update('mainStreamUrl', event.target.value)} placeholder="rtsp://…/stream1" />
            </label>
            <label className="field-group field-group--secret">
              <span>Дополнительный поток</span>
              <input type={showStreams ? 'text' : 'password'} inputMode="url" maxLength={500} autoComplete="off" autoCapitalize="none" spellCheck={false} value={form.subStreamUrl} onChange={(event) => update('subStreamUrl', event.target.value)} placeholder="rtsp://…/stream2" />
            </label>
            <button className="camera-form__reveal" type="button" onClick={() => setShowStreams((value) => !value)}>
              {showStreams ? <EyeOff size={15} /> : <Eye size={15} />} {showStreams ? 'Скрыть RTSP-адреса' : 'Показать RTSP-адреса'}
            </button>
            <label className="field-group">
              <span>Профиль / разрешение</span>
              <input maxLength={200} value={form.onvifProfile} onChange={(event) => update('onvifProfile', event.target.value)} placeholder="Например, 1920×1080" />
            </label>
            <label className="switch-control camera-form__switch">
              <input type="checkbox" checked={form.supportsPtz} onChange={(event) => update('supportsPtz', event.target.checked)} />
              <span aria-hidden="true" />
              <em>Камера поддерживает PTZ</em>
            </label>
          </div>
        </details>

        <p className="camera-form__security-note">RTSP-адреса и пароль шифруются и не возвращаются обратно в браузер.</p>
        {error && <p className="camera-form__error" role="alert">{error}</p>}
      </div>
      <footer className="camera-dialog__actions">
        <button className="button button--secondary" type="button" onClick={onCancel}>Отмена</button>
        <button className="button button--primary" type="submit" disabled={isSubmitting}>{isSubmitting ? 'Добавление…' : 'Добавить камеру'}</button>
      </footer>
    </form>
  );
}
